import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithGoogle } from '../../../Services/auth';
import { handlePostLoginFlow } from '../../../Utils/postLoginHandler';
import { getUserProfile } from '../../../Services/MyProfile';
import { useHeader } from '../../../Providers/HeaderContextProvider';
import InlineErrorMessage from '../../../Components/InlineErrorMessage';
import { ReactComponent as GoogleIcon } from '../../../Assets/icon/google.svg';

const GoogleAuth = ({ onSuccess, onError }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addProfileData } = useHeader();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const redirectUri = searchParams.get('redirect_uri');

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleGoogleSignIn = async () => {
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      if (typeof window.google === 'undefined') {
        const errorMsg = 'Google Sign-In is not available. Please try again later.';
        setError(errorMsg);
        if (onError) onError(errorMsg);
        setLoading(false);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            const credential = response.credential;
            const payload = JSON.parse(atob(credential.split('.')[1]));
            
            const googleData = {
              id_token: credential,
              access_token: response.access_token || '',
              user_id: payload.sub,
              email: payload.email,
              email_verified: payload.email_verified,
              first_name: payload.given_name || '',
              last_name: payload.family_name || '',
              profile_picture: payload.picture || '',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };

            const result = await signInWithGoogle(googleData);
            
            if (result.status === 200 || result.status === 323 || result.status === 231) {
              if (addProfileData) {
                await addProfileData();
              }
              
              // Fetch fresh profile from API
              let profile = {};
              try {
                const freshProfile = await getUserProfile();
                if (freshProfile) {
                  profile = freshProfile;
                  localStorage.setItem('profile', JSON.stringify(freshProfile));
                }
              } catch (err) {
                console.error('Error fetching profile:', err);
                profile = JSON.parse(localStorage.getItem('profile') || '{}');
              }
              
              // Check onboarding status
              const onboardingSteps = profile?.onboarding_step || [];
              const hasCompletedOnboarding = Array.isArray(onboardingSteps) && 
                onboardingSteps.some(s => String(s).toLowerCase() === 'completed');
              const hasSubscription = Array.isArray(onboardingSteps) && 
                onboardingSteps.some(s => String(s).toLowerCase() === 'subscription');
              const isVerified = profile?.is_verified !== false;
              
              if (!isVerified || !hasCompletedOnboarding || !hasSubscription) {
                // New or unverified user - navigate to QR page
                const queryString = searchParams.toString();
                navigate(`/login/qr${queryString ? '?' + queryString : ''}`);
              } else {
                // Existing verified user
                if (onSuccess) {
                  onSuccess(result);
                } else {
                  await handlePostLoginFlow({ navigate, addProfileData, redirectUri });
                }
              }
            } else {
              const errorMsg = 'Sign-in failed. Please try again.';
              setError(errorMsg);
              if (onError) onError(errorMsg);
            }
          } catch (error) {
            console.error('Google sign-in error:', error);
            const errorMsg = error.message || 'Google sign-in failed. Please try again.';
            setError(errorMsg);
            if (onError) onError(errorMsg);
          } finally {
            setLoading(false);
          }
        },
      });

      window.google.accounts.id.prompt();
    } catch (error) {
      console.error('Google sign-in initialization error:', error);
      const errorMsg = 'Failed to initialize Google Sign-In. Please try again.';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <>
      <button 
        className="auth-method-btn"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        <GoogleIcon style={{ width: '18px', height: '18px', marginRight: '0px' }} />
        <span style={{ flex: 1, textAlign: 'center' }}>
          {loading ? 'Signing in...' : 'Continue with Google'}
        </span>
      </button>
      {error && <InlineErrorMessage message={error} />}
    </>
  );
};

export default GoogleAuth;
